import React, { useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const DownloadModal = ({
  open,
  onClose,
  selectedDownloadOption,
  selectedDate,
  startDate,
  endDate,
  selectedTradeStatus,
  onDownloadOptionSelect,
  onDateChange,
  onStartDateChange,
  onEndDateChange,
  onTradeStatusChange,
  onNextPreview,
  isFormValid,
  isLoading = false,
  ordersCount = 0
}) => {
 

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="download-trades-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h4" sx={{
              color: '#141218',
              fontFamily: 'Founders Grotesk, sans-serif',
              fontSize: '24px',
              fontWeight: '600',
              margin: '0'
            }}>
              Download Trades
            </Typography>
            {isLoading ? (
              <Typography sx={{
                color: '#1344EB',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Loading orders...
              </Typography>
            ) : ordersCount > 0 ? (
              <Typography sx={{
                color: '#666',
                fontSize: '14px',
                fontWeight: '400'
              }}>
                {ordersCount} pending orders found
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Subtitle */}
        <Typography sx={{
          color: '#545454',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          fontWeight: '400',
          margin: '8px 24px 24px 24px',
          lineHeight: '1.4'
        }}>
          Select option to download trade file for
        </Typography>

        {/* Download Options */}
        <Box sx={{ padding: '0 24px' }}>
          {/* Download by Date */}
          <Box
            onClick={() => onDownloadOptionSelect('Download by Date')}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              margin: '0 0 12px 0',
              border: selectedDownloadOption === 'Download by Date' ? '2px solid #1344EB' : '1px solid #E6E6E6',
              borderRadius: '8px',
              backgroundColor: selectedDownloadOption === 'Download by Date' ? '#F8F9FF' : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1344EB',
                backgroundColor: selectedDownloadOption === 'Download by Date' ? '#F8F9FF' : '#FAFAFA'
              }
            }}
          >
            <Typography sx={{
              color: '#141218',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Download by Date
            </Typography>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: '2px solid #E6E6E6',
              backgroundColor: selectedDownloadOption === 'Download by Date' ? '#1344EB' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {selectedDownloadOption === 'Download by Date' && (
                <Box sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white'
                }} />
              )}
            </Box>
          </Box>

          {/* Date Input Section for Download by Date */}
          {selectedDownloadOption === 'Download by Date' && (
            <Box sx={{
              margin: '0 0 12px 0',
              padding: '20px',
              border: '2px solid #1344EB',
              borderRadius: '8px',
              backgroundColor: '#F8F9FF',
              width: '100%'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                margin: '0 0 16px 0'
              }}>
                Select the date against which you'd like to download trades
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%'
              }}>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => onDateChange(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #E6E6E6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    width: '100%',
                    maxWidth: '300px'
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Download by Time Period */}
          <Box
            onClick={() => onDownloadOptionSelect('Download by Time Period')}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              margin: '0 0 12px 0',
              border: selectedDownloadOption === 'Download by Time Period' ? '2px solid #1344EB' : '1px solid #E6E6E6',
              borderRadius: '8px',
              backgroundColor: selectedDownloadOption === 'Download by Time Period' ? '#F8F9FF' : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1344EB',
                backgroundColor: selectedDownloadOption === 'Download by Time Period' ? '#F8F9FF' : '#FAFAFA'
              }
            }}
          >
            <Typography sx={{
              color: '#141218',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Download by Time Period
            </Typography>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: '2px solid #E6E6E6',
              backgroundColor: selectedDownloadOption === 'Download by Time Period' ? '#1344EB' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {selectedDownloadOption === 'Download by Time Period' && (
                <Box sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white'
                }} />
              )}
            </Box>
          </Box>

          {/* Date Range Input Section for Download by Time Period */}
          {selectedDownloadOption === 'Download by Time Period' && (
            <Box sx={{
              margin: '0 0 12px 0',
              padding: '20px',
              border: '2px solid #1344EB',
              borderRadius: '8px',
              backgroundColor: '#F8F9FF',
              width: '100%'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                margin: '0 0 16px 0'
              }}>
                Select the range against which you'd like to download trades
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%'
              }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #E6E6E6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    flex: '1',
                    minWidth: '0'
                  }}
                />
                <Typography sx={{
                  color: '#666',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  flexShrink: 0
                }}>
                  to
                </Typography>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #E6E6E6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    flex: '1',
                    minWidth: '0'
                  }}
                />
              </Box>
            </Box>
          )}

      
          {/* Trade Status Dropdown Section */}
          {selectedDownloadOption === 'Download by Trade Status' && (
            <Box sx={{
              margin: '0 0 12px 0',
              padding: '20px',
              border: '2px solid #1344EB',
              borderRadius: '8px',
              backgroundColor: '#F8F9FF',
              width: '100%'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                margin: '0 0 16px 0'
              }}>
                Select the trade status below
              </Typography>
              <select
                value={selectedTradeStatus}
                onChange={(e) => {
                 
                  onTradeStatusChange(e.target.value);
                }}
                style={{
                  padding: '12px 16px',
                  border: '1px solid #E6E6E6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Roboto, sans-serif',
                  width: '100%',
                  maxWidth: '300px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Box>
          )}

          {/* Download Specific Trades */}
          <Box
            onClick={() => onDownloadOptionSelect('Download Specific Trades')}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              margin: '0 0 12px 0',
              border: selectedDownloadOption === 'Download Specific Trades' ? '2px solid #1344EB' : '1px solid #E6E6E6',
              borderRadius: '8px',
              backgroundColor: selectedDownloadOption === 'Download Specific Trades' ? '#F8F9FF' : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1344EB',
                backgroundColor: selectedDownloadOption === 'Download Specific Trades' ? '#F8F9FF' : '#FAFAFA'
              }
            }}
          >
            <Typography sx={{
              color: '#141218',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Download Specific Trades
            </Typography>
            <Box sx={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: '2px solid #E6E6E6',
              backgroundColor: selectedDownloadOption === 'Download Specific Trades' ? '#1344EB' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {selectedDownloadOption === 'Download Specific Trades' && (
                <Box sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white'
                }} />
              )}
            </Box>
          </Box>
        </Box>

        {/* Action Button */}
        <Box sx={{
          padding: '24px',
          borderTop: '1px solid #E6E6E6',
          backgroundColor: '#FAFAFA'
        }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              onNextPreview();
            }}
            disabled={!isFormValid()}
            sx={{
              height: '48px',
              backgroundColor: isFormValid() ? '#1344EB' : '#E6E6E6',
              color: isFormValid() ? 'white' : '#999',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '16px',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: isFormValid() ? '#0f3ad1' : '#E6E6E6'
              },
              '&:disabled': {
                backgroundColor: '#E6E6E6',
                color: '#999'
              }
            }}
          >
            Next, Preview Downloading Trades
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DownloadModal; 